import { RubricOutline } from '@/components/RubricOutline'
import { useRubricRevealRequest } from '@/components/reviews/rubricLinks'
import type { ExpectedValue, ImplementedCheck, ImplementedRubricSpec, Scalar } from '@/lib/types'

const valueText = (value: Scalar) => typeof value === 'number' ? Number(value.toPrecision(12)).toString()
  : typeof value === 'boolean' ? value ? 'Yes' : 'No' : value

/** Describe the published comparisons; never infer rules or recalculate grades. */
export function acceptance(expected?: ExpectedValue): string {
  switch (expected?.kind) {
    case 'tolerance': return `${valueText(expected.target)} ± ${valueText(expected.tolerance)}`
    case 'range': return expected.min === undefined ? expected.max === undefined ? 'Not specified' : `At most ${valueText(expected.max)}`
      : expected.max === undefined ? `At least ${valueText(expected.min)}`
      : `${valueText(expected.min)} to ${valueText(expected.max)} (inclusive)`
    case 'equals': return valueText(expected.value)
    case 'one_of': return expected.values.map(valueText).join(' or ')
    case 'regex': return `Full text match: ${expected.pattern}`
    default: return 'Not specified'
  }
}

function judgeDetails(check: ImplementedCheck) {
  const raw = check.parameters?.judge
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const judge = raw as Record<string, unknown>
  return {
    question: typeof judge.question === 'string' ? judge.question : '',
    context: typeof judge.context === 'string' ? judge.context : '',
    votes: typeof judge.votes === 'number' ? judge.votes : undefined,
  }
}

export function RubricScoring({ aggregation }: { aggregation: ImplementedRubricSpec['aggregation'] }) {
  const components = Object.entries(aggregation.components)
  const summary = aggregation.method === 'gated_weighted'
    ? 'A failed required check gives a score of 0. Otherwise, the score is a weighted average.'
    : aggregation.method === 'binary' ? 'The score is 1 if all required checks pass. Otherwise, it is 0.'
    : aggregation.reward_summary
  return <>
    <p className="science-rubric-summary">{summary}</p>
    {aggregation.method === 'weighted' && aggregation.failed_reward_cap !== undefined && <p className="small">If a required check fails, the score is at most {valueText(aggregation.failed_reward_cap)}.</p>}
    {components.length > 1 && <dl className="science-rubric-groups">{components.map(([name, share]) => <div key={name}><dt>{name}</dt><dd>Group weight {valueText(share)}</dd></div>)}</dl>}
  </>
}

export function TaskRubric({ spec, developer, showSummary = true }: { spec: ImplementedRubricSpec; developer: boolean; showSummary?: boolean }) {
  const revealRequest = useRubricRevealRequest(spec)
  const { aggregation, checks } = spec
  const components = Object.entries(aggregation.components)
  const groups = components.length > 1 ? components.map(([name], index) => ({
    section_id: `component-${index}`, title: `Scoring group: ${name}`, criterion_ids: checks.filter(c => c.component === name).map(c => c.id),
  })) : [{ section_id: 'checks', title: '', criterion_ids: checks.map(c => c.id) }]
  const byId = new Map(checks.map(c => [c.id, c]))
  return <>
    {showSummary && <RubricScoring aggregation={aggregation} />}
    <RubricOutline sections={groups} revealRequest={revealRequest} text={id => {
      const check = byId.get(id)!
      return `${check.label} ${check.scoring_description ?? ''} ${acceptance(check.expected)} ${check.note ?? ''}`
    }}>{(_section, ids) => <ol className="implemented-checks science-rubric-checks" aria-label="Scoring checks">{ids.map(id => {
      const check = byId.get(id)!
      const index = checks.indexOf(check)
      const judge = judgeDetails(check)
      return <li key={check.id} data-rubric-check={check.id}>
        <span className="implemented-number" aria-hidden="true">{index + 1}</span>
        <div className="criterion-body">
          <div className="science-check-heading"><h3 className="implemented-label">{check.label}</h3><span className="small muted">Weight {valueText(check.weight)}</span></div>
          <div className="check-meta">{check.required_for_pass && <span className="chip neutral">Required to pass</span>}<span>{judge ? `LLM judge${judge.votes ? ` · ${judge.votes} votes` : ''}` : 'Automatic check'}</span>{components.length > 1 && <span>{check.component}</span>}</div>
          {check.scoring_description && <p className="science-check-description">{check.scoring_description.replace(/^Binary:\s*/, '')}</p>}
          <dl className="science-check-rule"><div><dt>Accepted result</dt><dd title={check.expected ? Object.values(check.expected).filter(value => typeof value === 'number').join(' · ') || undefined : undefined}>{acceptance(check.expected)}</dd></div></dl>
          {judge?.question && <div className="science-judge-question"><h4>Judge question</h4><p>{judge.question}</p>{judge.context && <><h4>Reference reasoning</h4><p>{judge.context}</p></>}</div>}
          {check.note && <p className="small muted">{check.note}</p>}
          {developer && <div className="science-check-source small muted"><code>{check.id}</code>{check.state_path && <span>State field <code>{check.state_path}</code></span>}</div>}
        </div>
      </li>
    })}</ol>}</RubricOutline>
  </>
}
